import React, { useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { closeSolveHomeworkModel } from "../../../modules/admin-modules/models/Actions";
import { solveHomeworkRequest } from "../../../modules/students-modules/homework/Actions";

const FONT_STACK = `'Tajawal','Amiri','Scheherazade New','Noto Sans Arabic','Cairo',sans-serif`;

const C = {
  primary: "#6A1E1E",
  accent: "#4A0F1F",
  pageBg: "#E9E2DB",
  border: "#D8CEC3",
  cream: "#FAF2EB",
  ink: "#1e1b17",
  muted: "rgba(74, 15, 31, 0.65)",
};

const SolveHomework = ({ data, setSelectedData, onSubmitted }) => {
  const dispatch = useDispatch();
  const isOpen = useSelector((state) => state.model.isOpen23);
  const isLoading = useSelector((state) => state.questionsBank.isLoading);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: { comment: "", file: undefined },
    shouldUnregister: true,
  });

  const fileName = watch("file");

  useEffect(() => {
    if (!data?.id) return;
    reset({
      comment: data?.comment ?? "",
      file: undefined,
    });
  }, [data?.id, data?.comment, reset]);

  const closeFunc = () => {
    dispatch(closeSolveHomeworkModel());
    setSelectedData({});
    reset({ comment: "", file: undefined });
  };

  const onSubmit = async (values) => {
    const action = () => {
      dispatch(closeSolveHomeworkModel());
      setSelectedData({});
      reset({ comment: "", file: undefined });
      onSubmitted?.();
    };
    const formData = new FormData();
    formData.append("file", values?.file);
    formData.append("comment", values?.comment ?? "");
    formData.append("homework_id", data?.id);
    dispatch(
      solveHomeworkRequest({
        formData: formData,
        action: action,
      })
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={closeFunc} isCentered size="lg" motionPreset="slideInBottom">
      <ModalOverlay bg="rgba(74, 15, 31, 0.55)" backdropFilter="blur(8px)" />
      <ModalContent
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        fontFamily={FONT_STACK}
        color={C.ink}
        bg={C.cream}
        borderRadius="2xl"
        borderWidth="1px"
        borderColor={C.border}
        boxShadow="0 25px 50px -12px rgba(74, 15, 31, 0.25)"
        mx={3}
        overflow="hidden"
        position="relative"
      >
        <Box position="absolute" left={0} top={0} bottom={0} w="6px" bg={C.primary} borderLeftRadius="2xl" />
        <ModalCloseButton
          top={4}
          right={4}
          borderRadius="lg"
          color={C.accent}
          _hover={{ bg: "rgba(106, 30, 30, 0.08)" }}
          onClick={closeFunc}
        />
        <ModalHeader pt={8} pr={14} pb={2} pl={8}>
          <Text
            fontSize="10px"
            fontWeight="800"
            color={C.muted}
            textTransform="uppercase"
            letterSpacing="0.18em"
            mb={2}
          >
            Homework
          </Text>
          <Text fontSize="xl" fontWeight="800" color={C.primary} lineHeight="short" textTransform="capitalize">
            {data?.name || "Submit homework"}
          </Text>
          <Text fontSize="sm" fontWeight="500" color={C.muted} mt={2} lineHeight="1.5">
            Upload your answer as a PDF and add an optional note for your instructor.
          </Text>
        </ModalHeader>

        <ModalBody px={8} pt={4} pb={2}>
          <VStack align="stretch" spacing={6}>
            <Box>
              <FormLabel
                htmlFor="hw-comment"
                fontSize="10px"
                fontWeight="800"
                color={C.accent}
                textTransform="uppercase"
                letterSpacing="0.12em"
                mb={2}
              >
                Message / notes
              </FormLabel>
              <Textarea
                id="hw-comment"
                placeholder="Add context or questions for your instructor (optional)"
                borderRadius="xl"
                borderWidth="1px"
                borderColor={C.border}
                bg="white"
                minH="120px"
                fontSize="md"
                _placeholder={{ color: "rgba(74, 15, 31, 0.35)" }}
                _hover={{ borderColor: `${C.primary}66` }}
                _focus={{ borderColor: C.primary, boxShadow: `0 0 0 1px ${C.primary}` }}
                {...register("comment")}
              />
              {errors?.comment?.message ? (
                <Text fontSize="xs" color="red.600" mt={1}>
                  {errors.comment.message}
                </Text>
              ) : null}
            </Box>

            <Box>
              <FormLabel
                fontSize="10px"
                fontWeight="800"
                color={C.accent}
                textTransform="uppercase"
                letterSpacing="0.12em"
                mb={2}
              >
                Answer file
              </FormLabel>
              <Flex
                align="center"
                gap={3}
                flexWrap="wrap"
                p={4}
                borderRadius="xl"
                borderWidth="2px"
                borderStyle="dashed"
                borderColor={C.border}
                bg="white"
              >
                <Button
                  as="label"
                  cursor="pointer"
                  bg={C.primary}
                  color="white"
                  borderRadius="xl"
                  fontWeight="800"
                  textTransform="uppercase"
                  letterSpacing="0.08em"
                  fontSize="xs"
                  px={6}
                  py={5}
                  position="relative"
                  overflow="hidden"
                  _hover={{ bg: C.accent }}
                  flexShrink={0}
                >
                  Choose PDF
                  <Input
                    onChange={(e) => {
                      setValue("file", e.target.files?.[0], { shouldValidate: true });
                    }}
                    accept=".pdf,application/pdf"
                    type="file"
                    position="absolute"
                    inset={0}
                    opacity={0}
                    cursor="pointer"
                    w="100%"
                    h="100%"
                  />
                </Button>
                <Text fontSize="sm" fontWeight="600" color={C.muted} noOfLines={2} flex={1} minW={0}>
                  {fileName?.name ? fileName.name : "No file selected"}
                </Text>
              </Flex>
              <Text fontSize="xs" fontWeight="600" color={C.muted} mt={2}>
                PDF only · max 5 MB
              </Text>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter px={8} pb={8} pt={4} gap={3} flexWrap="wrap">
          <Button
            type="button"
            variant="outline"
            borderWidth="2px"
            borderColor={C.primary}
            color={C.primary}
            borderRadius="xl"
            fontWeight="800"
            textTransform="uppercase"
            letterSpacing="0.1em"
            fontSize="xs"
            flex={{ base: "1 1 100%", sm: "0 0 auto" }}
            px={8}
            py={6}
            _hover={{ bg: "rgba(106, 30, 30, 0.06)" }}
            onClick={closeFunc}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            bg={C.primary}
            color="white"
            borderRadius="xl"
            fontWeight="800"
            textTransform="uppercase"
            letterSpacing="0.1em"
            fontSize="xs"
            flex={{ base: "1 1 100%", sm: "1" }}
            px={8}
            py={6}
            isLoading={isLoading}
            loadingText="Submitting"
            _hover={{ bg: C.accent }}
            boxShadow="0 10px 24px rgba(106, 30, 30, 0.2)"
          >
            Submit homework
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SolveHomework;
